/* eslint-disable max-len */
import {
    Box,
    Button,
    FormControl,
    FormHelperText,
    Heading,
    Spinner,
    Text,
    VStack,
} from '@chakra-ui/react';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert } from '@acp/core-ui';
import { NotificationPreferenceType, StateShortName } from '@acp/shared';
import { useSession } from '@acp/feature/auth';
import { useState } from 'react';
import {
    PropertyManagerSelect,
    useNotificationPreferences,
    usePropertyManager,
} from '../../../agency';
import {
    NotificationPreferenceFormValues,
    notificationPreferenceSchema,
    rmsEmailNotificationFields,
} from './form-schema';
import { useNotificationPreferencesUpdate } from '../../hooks/use-notification-preferences-update';
import { useUserSettings } from '../../hooks/use-user-settings';
import { useEmailNotificationSettings } from '../../hooks/use-email-notification-settings';
import { useEmailNotificationSettingsUpdate } from '../../hooks/use-email-notification-settings-update';
import { AutoSendServiceReportsPreference } from './auto-send-service-reports-preference';
import { RmsEmailNotificationPreferences } from './rms-email-notification-preferences';

function NotificationPreferencesForm({
    propertyManagerId,
    propertyManagerEmail,
    propertyManagerName,
    isAutoSendServiceReportsDisabled,
    defaultValues,
}: {
    propertyManagerId: number;
    propertyManagerEmail: string;
    propertyManagerName: string;
    isAutoSendServiceReportsDisabled: boolean;
    defaultValues: NotificationPreferenceFormValues;
}) {
    const { complianceAgencyState } = useSession();
    const updateMutation = useNotificationPreferencesUpdate();
    const emailUpdateMutation = useEmailNotificationSettingsUpdate();

    // RMS (Minimum Standards) is only offered to Victorian agencies
    const isVic = complianceAgencyState.toLocaleLowerCase() === StateShortName.VIC;

    const form = useForm<NotificationPreferenceFormValues>({
        resolver: zodResolver(notificationPreferenceSchema),
        defaultValues,
        mode: 'onChange',
    });

    const { handleSubmit, formState: { isDirty, dirtyFields } } = form;

    const onSubmit = async (values: NotificationPreferenceFormValues) => {
        try {
            if (rmsEmailNotificationFields.some((field) => dirtyFields[field] === true)) {
                await emailUpdateMutation.update({
                    email: propertyManagerEmail,
                    rmsWorkOrderReceivedEnabled: values.rmsWorkOrderReceivedEnabled,
                    rmsCheckScheduledEnabled: values.rmsCheckScheduledEnabled,
                    rmsCheckCancelledEnabled: values.rmsCheckCancelledEnabled,
                });
            }

            if (dirtyFields.serviceReportsAutoOwnerEmailPreference === true) {
                await updateMutation.update({
                    propertyManagerId,
                    serviceReportsAutoOwnerEmailPreference: values.serviceReportsAutoOwnerEmailPreference,
                });
            }

            form.reset(values);
        } catch {
            // error toasts are shown by the mutation hooks
        }
    };

    return (
        <FormProvider {...form}>
            {!isAutoSendServiceReportsDisabled && (
                <AutoSendServiceReportsPreference />
            )}

            {isVic && (
                <RmsEmailNotificationPreferences propertyManagerName={propertyManagerName} />
            )}

            <Button
                w="fit-content"
                mt={4}
                onClick={handleSubmit(onSubmit)}
                isDisabled={!isDirty}
                isLoading={updateMutation.isPending || emailUpdateMutation.isPending}
            >
                Save changes
            </Button>
        </FormProvider>
    );
}

export function NotificationPreferences() {
    const preferences = useNotificationPreferences();
    const userSettings = useUserSettings();

    // use the agency's default PM until the user picks another one
    const [selectedPropertyManagerId, setSelectedPropertyManagerId] = useState<number | null>(null);
    const propertyManagerId = selectedPropertyManagerId
        ?? userSettings.data?.agency.defaultPropertyManagerId
        ?? null;

    // RMS email settings are stored against the PM's email address
    const { propertyManager } = usePropertyManager(propertyManagerId);
    const emailSettings = useEmailNotificationSettings(propertyManager?.email ?? null);

    const isAutoSendServiceReportsDisabled = preferences.data?.isServiceReportsAutoOwnerEmailAllowed === false;

    const renderForm = () => {
        if (propertyManagerId === null) {
            return null;
        }

        if (preferences.data === undefined || propertyManager === null || emailSettings.data === undefined) {
            return <Spinner color="primary.500" />;
        }

        const preference = preferences.data.preferences
            .find((p) => p.propertyManagerId === propertyManagerId.toString());

        return (
            <NotificationPreferencesForm
                key={propertyManagerId}
                propertyManagerId={propertyManagerId}
                propertyManagerEmail={propertyManager.email}
                propertyManagerName={propertyManager.name}
                isAutoSendServiceReportsDisabled={isAutoSendServiceReportsDisabled}
                defaultValues={{
                    serviceReportsAutoOwnerEmailPreference:
                        preference?.serviceReportsAutoOwnerEmailPreference
                        ?? NotificationPreferenceType.NotAllowed,
                    rmsWorkOrderReceivedEnabled: emailSettings.data.rmsWorkOrderReceivedEnabled,
                    rmsCheckScheduledEnabled: emailSettings.data.rmsCheckScheduledEnabled,
                    rmsCheckCancelledEnabled: emailSettings.data.rmsCheckCancelledEnabled,
                }}
            />
        );
    };

    return (
        <>
            <Heading size="md">Notification preferences</Heading>

            <VStack maxW={['initial', null, null, 'full']} align="stretch" gap={8}>
                {isAutoSendServiceReportsDisabled && (
                    <Alert title="Automatically sending service report emails is disabled" status="error">
                        The option to automatically send service reports to owners has been turned off by the agency.
                    </Alert>
                )}

                <FormControl>
                    <FormHelperText color="muted.500">
                        <Text>
                            Select a property manager to configure
                        </Text>
                    </FormHelperText>
                    <Box w="fit-content" mt={4} minW={['70%', null, '50%']}>
                        <PropertyManagerSelect
                            onChange={(option) => {
                                setSelectedPropertyManagerId(option !== null ? Number(option.value) : null);
                            }}
                            value={propertyManagerId?.toString() ?? null}
                            isClearable={false}
                        />
                    </Box>
                </FormControl>

                {renderForm()}
            </VStack>
        </>
    );
}
